import React from 'react';
import Icon, { type IconName } from '../primitives/Icon';
import styles from './BottomNavBar.module.css';

export interface BottomNavItem {
  label: string;
  /** 디자인 시스템 아이콘 (활성 상태). 지정 시 Icon 컴포넌트로 렌더. */
  iconName?: IconName;
  /** 비활성 상태 아이콘. 미지정 시 iconName 을 그대로 쓰고 opacity 로만 구분. */
  iconNameInactive?: IconName;
  /** iconName 미지정 시 폴백 텍스트/이모지. */
  icon: string;
  /** 아이콘 좌우 반전 (쿠지 티켓 — Figma 정본이 scaleX(-1) 적용). */
  flipX?: boolean;
  /**
   * 중앙 강조 탭 — 기획확정안 §7 "스캔을 중앙에서 가장 눈에 띄게".
   * 아이콘을 원형 accent 배경 위에 크게 올린다.
   */
  emphasized?: boolean;
}

interface BottomNavBarProps {
  activeIndex?: number;
  /**
   * \uD0ED \uBAA9\uB85D. \uC815\uBCF8\uC740 Layout.tsx \uC758 TABS \uB2E4 \u2014 \uB77C\uBCA8 i18n \uACFC \uC778\uC99D \uBD84\uAE30\uAC00 \uAC70\uAE30 \uC788\uC73C\uBBC0\uB85C
   * \uC774 \uCEF4\uD3EC\uB10C\uD2B8\uB294 \uAE30\uBCF8\uAC12\uC744 \uB4E4\uACE0 \uC788\uC9C0 \uC54A\uB294\uB2E4.
   * (2026-08-12: \uD15C\uD50C\uB9BF \uC2DC\uC808 \uAE30\uBCF8\uAC12 \uAC00\uCC28\uC0F5\u00B7\uCFE0\uC9C0\uC0F5\u00B7\uD53D\uCFE0\uBC15\uC2A4\uB97C \uC81C\uAC70\uD588\uB2E4.)
   */
  items: BottomNavItem[];
  onTap?: (index: number) => void;
}

const BottomNavBar: React.FC<BottomNavBarProps> = ({
  activeIndex = 0,
  items,
  onTap,
}) => {
  const navItems = items;

  /**
   * 강조 항목(스캔)은 막대 **밖**에 따로 선다 (2026-08-19).
   *
   * 이 앱이 하는 일 하나를 고르라면 스캔이다. 다섯 칸 중 하나로 줄 안에 서 있으면
   * 나머지 넷과 같은 무게가 되고, 가운데에서 위로 튀어나온 모양은 "줄에 속하는데
   * 튀어나온" 어중간한 상태였다. 아예 떼어 내면 '탐색' 과 '주 액션' 이 두 덩어리로
   * 갈린다.
   *
   * ⚠ onTap 은 **원래 인덱스**를 넘겨야 한다. 두 묶음으로 나눠 그린다고 번호를
   *   다시 매기면 탭이 엉뚱한 화면으로 간다 — 그래서 filter 가 아니라 map 안에서
   *   가른다.
   */
  const renderItem = (item: BottomNavItem, index: number) => {
    const isActive = index === activeIndex;
    const iconName = isActive ? item.iconName : (item.iconNameInactive ?? item.iconName);
    return (
      <button
        key={index}
        className={`${styles.navItem} ${item.emphasized ? styles.navItemEmphasized : ''}`}
        onClick={() => onTap?.(index)}
        // 글자 라벨을 뺀 뒤로(2026-08-19) 이름은 **여기에만** 남는다.
        // 스크린리더가 읽을 것이 없으면 아이콘만 있는 막대는
        // "버튼 버튼 버튼" 이 된다.
        aria-label={item.label}
        aria-current={isActive ? "page" : undefined}
      >
        <span
          className={[
            styles.navIcon,
            item.emphasized ? styles.navIconEmphasized : '',
            isActive ? styles.navIconActive : styles.navIconInactive,
            item.flipX ? styles.navIconFlip : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {/* 이름은 버튼의 aria-label 이 든다 — 아이콘에도 달면 두 번 읽힌다 */}
          {iconName ? (
            <Icon name={iconName} size="lg" />
          ) : (
            item.icon
          )}
        </span>
      </button>
    );
  };

  return (
    <div className={styles.bottomNavRow}>
      <div className={styles.bottomNavBar}>
        {navItems.map((item, index) => (item.emphasized ? null : renderItem(item, index)))}
      </div>
      {navItems.map((item, index) =>
        item.emphasized ? (
          <div key={`standalone-${index}`} className={styles.navStandalone}>
            {renderItem(item, index)}
          </div>
        ) : null,
      )}
    </div>
  );
};

export default BottomNavBar;
